/**
 * Items — bases, rarity tiers, and the shape of a dropped item.
 *
 * This module is the vocabulary the rest of the game speaks about gear: the
 * inventory grid reads `width`/`height` to pack an item, combat reads
 * `modifiers` for its stat contribution, and neither has to learn that affix
 * pools or drop tables exist.
 *
 * Everything below the type declarations is a DATA table. Adding a base, a
 * rarity colour or a stat is a table edit — never a new branch.
 *
 * A rolled `Item` is **self-contained**, which is why its gold value and its
 * wear state are minted here rather than derived at every call site: a vendor
 * pricing it, a repair costing it and a snapshot shipping it all read one
 * object. This module owns the *shapes* (`Durability`, `STAT_COST`); the rules
 * that move them live next door in `durability.ts`, the way the affix pools
 * live in `affixes.ts` while `ItemAffix` lives here.
 */
// The damage types live in `stats.ts`, which owns the combat vocabulary, and are
// re-exported here so item code reads naturally. One definition, two names for
// the same list would be a DRY violation waiting to drift apart.
export { DAMAGE_TYPES } from './stats.js';
/**
 * Rarity tiers, ascending. The order is load-bearing twice over: it is the
 * order a weighted drop roll walks, and it is the chain a tier downgrades along
 * when no content can satisfy it (D2 does the same when a base has no unique).
 */
export const RARITIES = ['junk', 'normal', 'magic', 'rare', 'set', 'unique'];
/** D2 structure, WoW palette. These hexes are the design doc's, not a preference. */
export const RARITY_COLORS = {
    junk: '#9d9d9d',
    normal: '#ffffff',
    magic: '#1eff00',
    rare: '#0070dd',
    set: '#a335ee',
    unique: '#ff8000',
};
/**
 * Every stat an item can contribute. Units are in the name: a `…Percent` stat
 * is a percentage, everything else is flat. `resistancePercent` at 100 or more
 * is an immunity, which is the combat layer's business to interpret.
 */
export const ITEM_STATS = [
    'damageMin',
    'damageMax',
    'enhancedDamagePercent',
    'attackRating',
    'defense',
    'enhancedDefensePercent',
    'resistancePercent',
    'blockChancePercent',
    'attackSpeedPercent',
    // D2's Faster Run/Walk. A percent, added across every source and spent once
    // by `movement.ts`'s `moveSpeed` — the single path gear, a buff, a chill and
    // an elite's Extra Fast all move a body's speed along.
    'movementSpeedPercent',
    'life',
    'mana',
    'strength',
    'dexterity',
    'vitality',
    'energy',
];
/** The stats that mean nothing without a damage type. Every other stat carries `null`. */
export const DAMAGE_TYPED_STATS = ['damageMin', 'damageMax', 'resistancePercent'];
/** D2's fixed divisor for the multiplier sum. Not a tuning knob — the formula's own constant. */
export const COST_MULTIPLIER_DIVISOR = 1024;
/** No item is ever worth nothing: a vendor pays *something* for junk, which is what the junk tier is for. */
export const MIN_ITEM_VALUE = 1;
export const STAT_COST = {
    damageMin: { multiply: 40, add: 8 },
    damageMax: { multiply: 40, add: 8 },
    enhancedDamagePercent: { multiply: 30, add: 6 },
    attackRating: { multiply: 3, add: 1 },
    defense: { multiply: 24, add: 4 },
    enhancedDefensePercent: { multiply: 30, add: 6 },
    resistancePercent: { multiply: 60, add: 12 },
    blockChancePercent: { multiply: 60, add: 15 },
    attackSpeedPercent: { multiply: 120, add: 40 },
    movementSpeedPercent: { multiply: 120, add: 40 },
    life: { multiply: 25, add: 5 },
    mana: { multiply: 25, add: 5 },
    strength: { multiply: 100, add: 20 },
    dexterity: { multiply: 100, add: 20 },
    vitality: { multiply: 100, add: 20 },
    energy: { multiply: 100, add: 20 },
};
/**
 * What a base is worth once `added` has been layered onto it.
 *
 * `added` is what the *roll* put on the item — affixes, and a set or unique
 * entry's authored modifiers. Deliberately **not** the item's aggregate: the
 * base's own stat line is already paid for in its authored price, and charging
 * for it twice would make a plain Full Plate cost more than its own price tag.
 *
 * A plain base therefore prices at exactly its `cost`, which is the identity
 * this function is pinned by.
 */
export const valueWithModifiers = (baseCost, added) => {
    if (!Number.isInteger(baseCost) || baseCost < 0) {
        throw new RangeError(`An item base costs a whole non-negative number of gold, received ${baseCost}`);
    }
    const scaled = added.reduce((total, modifier) => total + modifier.value * STAT_COST[modifier.stat].multiply, 0);
    const flat = added.reduce((total, modifier) => total + STAT_COST[modifier.stat].add, 0);
    return Math.max(MIN_ITEM_VALUE, Math.round(baseCost * (1 + scaled / COST_MULTIPLIER_DIVISOR) + flat));
};
export const ITEM_CATEGORIES = ['weapon', 'armor', 'shield', 'jewelry'];
export const EQUIPMENT_SLOTS = [
    'mainHand',
    'offHand',
    'head',
    'chest',
    'hands',
    'feet',
    'waist',
    'finger',
    'neck',
];
/** Widest and tallest an item may be on the 10×4 grid. Packing pressure is a mechanic, so these are hard limits. */
export const ITEM_MAX_WIDTH = 2;
export const ITEM_MAX_HEIGHT = 4;
export const AFFIX_KINDS = ['prefix', 'suffix'];
const NO_AFFIXES = {
    minPrefixes: 0,
    maxPrefixes: 0,
    minSuffixes: 0,
    maxSuffixes: 0,
    maxTotal: 0,
};
/**
 * The affix column of the design doc's rarity table, as data.
 * Set and unique roll nothing: their modifiers are authored, not rolled.
 */
export const RARITY_AFFIX_BUDGET = {
    junk: NO_AFFIXES,
    normal: NO_AFFIXES,
    magic: { minPrefixes: 1, maxPrefixes: 1, minSuffixes: 1, maxSuffixes: 1, maxTotal: 2 },
    rare: { minPrefixes: 1, maxPrefixes: 3, minSuffixes: 1, maxSuffixes: 3, maxTotal: 6 },
    set: NO_AFFIXES,
    unique: NO_AFFIXES,
};
/**
 * Set and unique content is unwritten (TODO 24). Both tables are deliberately
 * empty rather than filled with placeholders: the loot roll downgrades a tier
 * it cannot satisfy, so an empty table means those tiers simply never drop yet
 * — instead of dropping a purple item with nothing on it.
 */
export const FIXED_ITEMS = [];
export const SET_BONUSES = [];
const damage = (min, max, damageType = 'physical') => [
    { stat: 'damageMin', damageType, value: min },
    { stat: 'damageMax', damageType, value: max },
];
const flat = (stat, value) => ({ stat, damageType: null, value });
/**
 * The base item table. Footprints follow D2's silhouettes — a great sword eats
 * a full 2×4 column of the 10×4 grid, a ring costs one cell — because that
 * pressure is the inventory mechanic.
 *
 * `cost` and `durability` are **placeholders** (`TODO.md` #380): invented on a
 * D2-shaped scale, not read out of D2's `weapons.txt` and `armor.txt`. Their
 * *shape* is not a placeholder — jewellery carries `null` because D2 gives
 * rings and amulets no durability at all, and that is a rule rather than a
 * number.
 */
export const ITEM_BASES = [
    // Weapons
    { id: 'dagger', name: 'Dagger', category: 'weapon', slot: 'mainHand', width: 1, height: 2, qualityLevel: 1, requiredLevel: 1, cost: 60, durability: 16, modifiers: damage(1, 4) },
    { id: 'short-sword', name: 'Short Sword', category: 'weapon', slot: 'mainHand', width: 1, height: 3, qualityLevel: 1, requiredLevel: 1, cost: 100, durability: 24, modifiers: damage(2, 7) },
    { id: 'wand', name: 'Wand', category: 'weapon', slot: 'mainHand', width: 1, height: 2, qualityLevel: 3, requiredLevel: 3, cost: 150, durability: 15, modifiers: [...damage(2, 4), flat('mana', 5)] },
    { id: 'short-bow', name: 'Short Bow', category: 'weapon', slot: 'mainHand', width: 2, height: 3, qualityLevel: 5, requiredLevel: 4, cost: 200, durability: 24, modifiers: damage(1, 6) },
    { id: 'quarterstaff', name: 'Quarterstaff', category: 'weapon', slot: 'mainHand', width: 1, height: 4, qualityLevel: 6, requiredLevel: 5, cost: 180, durability: 20, modifiers: [...damage(3, 9), flat('mana', 10)] },
    { id: 'long-sword', name: 'Long Sword', category: 'weapon', slot: 'mainHand', width: 1, height: 3, qualityLevel: 9, requiredLevel: 8, cost: 400, durability: 44, modifiers: damage(3, 12) },
    { id: 'war-axe', name: 'War Axe', category: 'weapon', slot: 'mainHand', width: 2, height: 3, qualityLevel: 14, requiredLevel: 12, cost: 900, durability: 45, modifiers: damage(10, 24) },
    { id: 'great-sword', name: 'Great Sword', category: 'weapon', slot: 'mainHand', width: 2, height: 4, qualityLevel: 22, requiredLevel: 20, cost: 2_000, durability: 50, modifiers: damage(15, 40) },
    // Armor
    { id: 'cap', name: 'Cap', category: 'armor', slot: 'head', width: 2, height: 2, qualityLevel: 1, requiredLevel: 1, cost: 120, durability: 12, modifiers: [flat('defense', 3)] },
    { id: 'quilted-armor', name: 'Quilted Armor', category: 'armor', slot: 'chest', width: 2, height: 3, qualityLevel: 1, requiredLevel: 1, cost: 200, durability: 20, modifiers: [flat('defense', 8)] },
    { id: 'leather-gloves', name: 'Leather Gloves', category: 'armor', slot: 'hands', width: 2, height: 2, qualityLevel: 1, requiredLevel: 1, cost: 150, durability: 12, modifiers: [flat('defense', 2)] },
    { id: 'leather-boots', name: 'Leather Boots', category: 'armor', slot: 'feet', width: 2, height: 2, qualityLevel: 1, requiredLevel: 1, cost: 150, durability: 12, modifiers: [flat('defense', 2)] },
    { id: 'sash', name: 'Sash', category: 'armor', slot: 'waist', width: 2, height: 1, qualityLevel: 1, requiredLevel: 1, cost: 100, durability: 12, modifiers: [flat('defense', 2)] },
    { id: 'heavy-belt', name: 'Heavy Belt', category: 'armor', slot: 'waist', width: 2, height: 1, qualityLevel: 12, requiredLevel: 10, cost: 500, durability: 18, modifiers: [flat('defense', 7)] },
    { id: 'ring-mail', name: 'Ring Mail', category: 'armor', slot: 'chest', width: 2, height: 3, qualityLevel: 13, requiredLevel: 11, cost: 900, durability: 26, modifiers: [flat('defense', 26)] },
    { id: 'helm', name: 'Helm', category: 'armor', slot: 'head', width: 2, height: 2, qualityLevel: 16, requiredLevel: 14, cost: 700, durability: 24, modifiers: [flat('defense', 15)] },
    { id: 'full-plate', name: 'Full Plate Mail', category: 'armor', slot: 'chest', width: 2, height: 3, qualityLevel: 30, requiredLevel: 26, cost: 4_000, durability: 60, modifiers: [flat('defense', 60)] },
    // Shields
    { id: 'buckler', name: 'Buckler', category: 'shield', slot: 'offHand', width: 2, height: 2, qualityLevel: 1, requiredLevel: 1, cost: 200, durability: 12, modifiers: [flat('defense', 4), flat('blockChancePercent', 10)] },
    { id: 'kite-shield', name: 'Kite Shield', category: 'shield', slot: 'offHand', width: 2, height: 3, qualityLevel: 14, requiredLevel: 12, cost: 800, durability: 30, modifiers: [flat('defense', 16), flat('blockChancePercent', 14)] },
    // Jewelry — no durability at all, D2's own rule for rings and amulets.
    { id: 'ring', name: 'Ring', category: 'jewelry', slot: 'finger', width: 1, height: 1, qualityLevel: 5, requiredLevel: 4, cost: 800, durability: null, modifiers: [] },
    { id: 'amulet', name: 'Amulet', category: 'jewelry', slot: 'neck', width: 1, height: 1, qualityLevel: 8, requiredLevel: 6, cost: 1_200, durability: null, modifiers: [] },
];
/** The base with this id, or `undefined`. Absence is not an error — the caller decides. */
export const itemBaseById = (id, bases = ITEM_BASES) => bases.find((entry) => entry.id === id);
/** Every base that may drop at this item level, D2's qlvl rule. */
export const basesAvailableAt = (itemLevel, bases = ITEM_BASES) => bases.filter((entry) => entry.qualityLevel <= itemLevel);
const modifierKey = (stat, damageType) => `${stat}:${damageType ?? 'any'}`;
/**
 * Merges contributions that address the same stat and damage type, in
 * first-appearance order. Deterministic ordering matters: items are compared,
 * hashed and shipped over the wire.
 */
export const aggregateModifiers = (modifiers) => {
    const merged = new Map();
    for (const modifier of modifiers) {
        const key = modifierKey(modifier.stat, modifier.damageType);
        const existing = merged.get(key);
        merged.set(key, existing === undefined ? modifier : { ...existing, value: existing.value + modifier.value });
    }
    return [...merged.values()];
};
/** What a modifier list contributes to one stat. Zero when it contributes nothing — combat should not special-case absence. */
export const modifierTotal = (modifiers, stat, damageType = null) => modifiers.reduce((total, modifier) => modifier.stat === stat && modifier.damageType === damageType ? total + modifier.value : total, 0);
const affixLabel = (affixes, kind) => affixes.find((entry) => entry.kind === kind)?.label ?? '';
/** Naming is per-rarity data, not a switch. Rare name tables are unwritten (TODO 25), so rares wear their base name. */
const NAME_BY_RARITY = {
    junk: (base) => base.name,
    normal: (base) => base.name,
    magic: (base, affixes) => [affixLabel(affixes, 'prefix'), base.name, affixLabel(affixes, 'suffix')]
        .filter((part) => part.length > 0)
        .join(' '),
    rare: (base) => base.name,
    set: (base) => base.name,
    unique: (base) => base.name,
};
const countKind = (affixes, kind) => affixes.filter((entry) => entry.kind === kind).length;
const assertWithinBudget = (rarity, affixes) => {
    const budget = RARITY_AFFIX_BUDGET[rarity];
    const prefixes = countKind(affixes, 'prefix');
    const suffixes = countKind(affixes, 'suffix');
    if (prefixes < budget.minPrefixes || prefixes > budget.maxPrefixes) {
        throw new RangeError(`A ${rarity} item takes ${budget.minPrefixes}–${budget.maxPrefixes} prefixes, received ${prefixes}`);
    }
    if (suffixes < budget.minSuffixes || suffixes > budget.maxSuffixes) {
        throw new RangeError(`A ${rarity} item takes ${budget.minSuffixes}–${budget.maxSuffixes} suffixes, received ${suffixes}`);
    }
    if (affixes.length > budget.maxTotal) {
        throw new RangeError(`A ${rarity} item takes at most ${budget.maxTotal} affixes, received ${affixes.length}`);
    }
    if (new Set(affixes.map((entry) => entry.id)).size !== affixes.length) {
        throw new RangeError(`An item cannot carry the same affix twice: ${affixes.map((e) => e.id).join(', ')}`);
    }
    if (new Set(affixes.map((entry) => entry.group)).size !== affixes.length) {
        throw new RangeError(`An item cannot carry two affixes from one group: ${affixes.map((e) => e.group).join(', ')}`);
    }
};
/**
 * Mints an item from a base, a rarity and its rolled affixes.
 *
 * The rarity table is enforced here rather than trusted to the roller, so
 * "magic is exactly one prefix and one suffix" and "rare caps at six" are
 * invariants of the type, not conventions of one call site.
 *
 * Price and durability are minted here too, for the same reason the aggregated
 * modifiers are: this is the one place that holds the base, the affixes and the
 * fixed entry at once, so it is the only place that can compute either without
 * looking something back up. Every item leaves here whole and worth something.
 */
export const createItem = ({ id, base, rarity, itemLevel, affixes, fixed }) => {
    assertWithinBudget(rarity, affixes);
    if (fixed !== null && fixed.baseId !== base.id) {
        throw new RangeError(`Fixed item ${fixed.id} belongs to base ${fixed.baseId}, not ${base.id}`);
    }
    // What the *roll* added, which is what gets priced on top of the authored
    // base cost. The base's own stat line is already in that price.
    const added = [
        ...affixes.flatMap((entry) => entry.modifiers),
        ...(fixed?.modifiers ?? []),
    ];
    return {
        id,
        baseId: base.id,
        name: fixed?.name ?? NAME_BY_RARITY[rarity](base, affixes),
        category: base.category,
        slot: base.slot,
        rarity,
        itemLevel,
        requiredLevel: fixed?.requiredLevel ?? base.requiredLevel,
        width: base.width,
        height: base.height,
        affixes,
        modifiers: aggregateModifiers([...base.modifiers, ...added]),
        value: valueWithModifiers(base.cost, added),
        durability: base.durability === null ? null : { current: base.durability, maximum: base.durability },
    };
};
//# sourceMappingURL=items.js.map